export default class SubstanceBar extends HTMLElement {
    #callbacks = {
        /**
         * @callback SubstanceBar.onSizeChangedCallbackType
         * @param {boolean} isMinimized
         */
        sizeChanged: (isMinimized) => {/* to be overridden */},
        /**
         * @callback SubstanceBar.onSubstanceSelectedCallbackType
         * @param {string} substanceId
         * @param {string} name
         * @param {string} color
         */
        substanceSelected: (substanceId, name, color) => {/* to be overridden */},
        /**
         * @callback SubstanceBar.onSubstanceDeselectedCallbackType
         * @param {string} substanceId
         */
        substanceDeselected: (substanceId) => {/* to be overridden */}
    }

    connectedCallback() {
        const template = document.createElement('template');
        template.innerHTML = this._template();
        this.attachShadow({mode: 'open'}).appendChild(template.content.cloneNode(true))

        this.shadowRoot.getElementById("toggle-size-btn").addEventListener('click', () => {
            this.setMinimized(!this.#isMinimized());
        });
        let resizeTimeout;
        window.addEventListener("resize", () => {
            if (!resizeTimeout) {
                resizeTimeout = setTimeout(() => {
                    resizeTimeout = null;
                    this._alignVertically();
                }, 500);
            }
        }, false);
        this._alignVertically();
    }

    setMinimized(isMinimized) {
        [   this,
            ...this.shadowRoot.querySelectorAll('bar-button'),
            ...this.querySelectorAll('substance-bar-item, bar-button')]
            .forEach(i => i.toggleAttribute('minimized', isMinimized));
        this.#callbacks.sizeChanged(isMinimized);
    }

    /**
     * @param {SubstanceBarItem} substanceItem
     * @param {number} index
     */
    addSubstanceItem(substanceItem, index) {
        substanceItem.setAttribute("slot", "substanceItem");
        if (this.hasAttribute('minimized'))
            substanceItem.setAttribute('minimized', true)
        const nextElement = this.children[index] || null;
        this.insertBefore(substanceItem, nextElement);
        substanceItem.addEventListener('click', () => {
            this._onSubstanceClick(substanceItem)
        })
        this._alignVertically();
        if(this.isSomeItemSelected())
            substanceItem.displayBackground();
    }

    /**
     * Updates the name and color of the substance item. This is necessary when the substance name or color changes
     * (e.g. when the user changes the name or color of the substance).
     * @param {string} substanceId - id of the substance, used to identify the item
     * @param {string} name
     * @param {string} color
     */
    updateSubstanceItem(substanceId, name, color){
        for (const item of this._getSubstanceBarItems())
            if (item.getData().substanceId === substanceId)
                item.setData({name, color, substanceId})
    }

    deleteSubstanceItem(substanceId){
        for (const item of this._getSubstanceBarItems())
            if (item.getData().substanceId === substanceId) {
                if (item.hasAttribute('selected')) {
                    this._onSubstanceClick(item)
                }
                item.remove();
            }
        this._alignVertically();
    }

    emptySubstanceList() {
        for (const element of this._getSubstanceBarItems())
            element.remove()
        this._alignVertically();
    }

    deselectAll() {
        for (const el of this._getSubstanceBarItems())
            el.displayNormal();
    }

    /**
     * @param {SubstanceBar.onSizeChangedCallbackType} callback
     */
    onSizeChanged(callback) {
        this.#callbacks.sizeChanged = callback;
    }

    /**
     * @param {SubstanceBar.onSubstanceSelectedCallbackType} callback
     */
    onSubstanceSelected(callback) {
        this.#callbacks.substanceSelected = callback;
    }

    /**
     * @param {SubstanceBar.onSubstanceDeselectedCallbackType} callback
     */
    onSubstanceDeselected(callback){
        this.#callbacks.substanceDeselected = callback;
    }

    /**
     * @param {SubstanceBarItem} element
     * @private
     */
    _onSubstanceClick(element) {
        let isSelected = element.hasAttribute('selected');
        for (const el of this._getSubstanceBarItems()) {
            if (isSelected){
                el.displayNormal();
                continue;
            }
            if (el === element)
                el.displayActive();
            else
                el.displayBackground();
        }
        isSelected = !isSelected;
        const {substanceId, name, color} = element.getData();
        isSelected
            ? this.#callbacks.substanceSelected(substanceId, name, color)
            : this.#callbacks.substanceDeselected(substanceId, name, color);
    }

    /** @returns {boolean} */
    isSomeItemSelected() {
        return !!this._getSelectedItem();
    }

    /** @returns {NodeListOf<SubstanceBarItem>} */
    _getSubstanceBarItems() {
        return this.querySelectorAll("substance-bar-item");
    }

    _getSelectedItem() {
        for (let item of this._getSubstanceBarItems())
            if(item.hasAttribute('selected'))
                return item;
        return null;
    }

    _template() {
        return `
              <style> @import "/ui/js/components/substance-bar/substanceBar.css?v=2"; </style>
              <div id="substance-list">
                <slot name="substanceItem"></slot>
              </div>
              <slot name="tourtip"></slot>
              <slot name="button"></slot>
              <bar-button id="toggle-size-btn" icon="arrows" text="Hide"></bar-button>
            `;
    }

    _alignVertically() {
        const align = this.getBoundingClientRect().height / 2 - 32;
        this.style.top = `calc(50% - ${align}px)`;
    }
    /**
     * Returns true if the substance bar is minimized, false otherwise.
     * @private
     * @returns {boolean}
     */
    #isMinimized() {
        return this.hasAttribute('minimized');
    }
}